import { useEffect, useRef, useState } from "react";
import * as api from "./v6Api";
import { bizFmt, phoneFmt, kstFull, kstShort, registrarLabel, groupByTeam, statusLabel, needsReason } from "./view";
import { StatusBadge, ChannelBadge } from "./Badges";
import { useDismiss } from "../v6common/a11y";
import PhotoAddModal from "./PhotoAddModal";

// 상세 서랍(계약 §4-1·§4-4). 모든 변경 응답은 갱신된 InquiryDetail 이라 그 응답으로 다시 그린다.
// 시안 규칙: 입력칸 변경분이 있으면 상태·담당자 동작 전에 PATCH 를 먼저 보내고 그 version 으로 이어 간다.
// 보류·취소는 사유(200자, 가맹점주에게 보인다)를 거친다 — 상태가 바뀔 때는 이번 요청의 reason 이 새 사유다.
const STALE_MSG = "다른 사람이 먼저 고쳤습니다. 최신 내용으로 바꿨어요. 다시 확인해 주세요.";
const FRESH_MSG = "다른 사람이 이 문의를 고쳤습니다. 입력 중인 내용은 그대로 두었어요.";
const PROC_MSG = "처리 내용을 먼저 적어야 완료로 바꿀 수 있어요. 가맹점주 앱에 이 내용이 그대로 보입니다.";
const REASON_MAX = 200;

const formOf = (d) => ({
    category: d.category?.code || "", sub: d.subcategory?.code || "",
    date: d.schedule?.date || "", time: d.schedule?.time || "",
    request: d.request_content || "", process: d.process_content || "",
});

function patchOf(d, f) {
    const body = {};
    if (f.category !== (d.category?.code || "") || f.sub !== (d.subcategory?.code || "")) {
        body.category_code = f.category;
        body.subcategory_code = f.sub;
    }
    // 서버는 빈 문자열 시간을 거절한다(time 은 null 이어야 한다)
    if (f.date !== (d.schedule?.date || "") || f.time !== (d.schedule?.time || "")) body.schedule = f.date ? { date: f.date, time: f.time || null } : null;
    if (f.request.trim() !== (d.request_content || "").trim()) body.request_content = f.request.trim();
    if (f.process.trim() !== (d.process_content || "").trim()) body.process_content = f.process.trim();
    return body;
}

const uploaderLabel = (a) => (a.uploaded_by?.type === "staff" ? a.uploaded_by.name || "직원" : "가맹점주");

export default function InquiryDrawer({ id, meta, staff, adminId, owner = false, refreshKey, onClose, onOpen, onChanged, notify, onGoChat }) {
    const [detail, setDetail] = useState(null);
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [procErr, setProcErr] = useState(false);
    const [msg, setMsg] = useState(""); // 서랍 안 경고(겹쳐 쓰기·오류)
    const [chat, setChat] = useState({ open: false, messages: undefined });
    const [fresh, setFresh] = useState(null); // 다른 사람이 고친 최신 상세(입력 중이라 아직 안 바꿈)
    const [reason, setReason] = useState("");
    const [pending, setPending] = useState(null); // 사유를 받는 중인 목표 상태(on_hold·canceled)
    const [picked, setPicked] = useState(null); // 사진 추가 창에 넘긴 파일들
    const fileRef = useRef(null);
    const dismiss = useDismiss(onClose, { enabled: !picked });
    const cur = useRef({ detail: null, form: null });
    cur.current = { detail, form };
    const applied = useRef(0); // 변경 응답을 반영할 때마다 올린다 — 그보다 먼저 시작한 조회 결과는 버린다

    useEffect(() => {
        let alive = true;
        const seq = applied.current;
        api.getDetail(id).then((d) => {
            if (!alive || seq !== applied.current) return;
            const { detail: old, form: f } = cur.current;
            if (old && f && Object.keys(patchOf(old, f)).length) { setFresh(d); return; }
            setDetail(d); setForm(formOf(d)); setFresh(null); setReason(d.status_reason || "");
        }).catch((e) => alive && setMsg(api.errorText(e)));
        return () => { alive = false; };
    }, [id, refreshKey]);
    useEffect(() => {
        api.markRead(id).catch(() => {});
        setChat({ open: false, messages: undefined });
        setMsg("");
        setProcErr(false);
        setPending(null);
    }, [id]);

    if (!detail || !form) {
        return (<><div className="dim on" {...dismiss}></div>
            <aside className="drawer on" aria-busy="true"><div className="dr-b"><div className="note">{msg || "불러오는 중…"}</div></div></aside></>);
    }

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));
    const apply = (d) => { applied.current += 1; setDetail(d); setForm(formOf(d)); setFresh(null); setReason(d.status_reason || ""); };
    const isBot = detail.channel === "chatbot";
    const cat = (meta?.categories || []).find((c) => c.code === form.category);
    const addable = groupByTeam(staff, { activeOnly: true })
        .map((g) => ({ ...g, members: g.members.filter((s) => !(detail.assignee_ids || []).includes(s.id)) }))
        .filter((g) => g.members.length);
    const staffName = (sid) => (staff || []).find((s) => s.id === sid)?.name || "직원";
    // 담당자는 한 명 — 이미 있으면 추가가 아니라 넘긴다
    const assignedIds = detail.assignee_ids || [];
    const current = assignedIds.length === 1 ? assignedIds[0] : null;
    const assignTo = (sid) => withFlush(
        () => (current == null ? api.addAssignee(id, sid) : api.transferAssignee(id, current, sid)),
        sid === adminId ? "내가 맡은 문의로 표시했습니다." : `${staffName(sid)}님을 담당자로 지정했습니다.`,
    );
    const images = (detail.attachments || []).filter((a) => (a.content_type || "").startsWith("image/"));
    const maxFiles = meta?.limits?.attachments_per_inquiry ?? 10;

    // 변경분을 먼저 저장하고 그 version 을 돌려준다(없으면 지금 version)
    async function flush() {
        const body = patchOf(detail, form);
        if (!Object.keys(body).length) return detail.version;
        if (form.time && !form.date) throw Object.assign(new Error("schedule"), { userMessage: "방문 날짜를 함께 골라 주세요." });
        const d = await api.editInquiry(id, { version: detail.version, ...body });
        apply(d);
        return d.version;
    }
    async function run(fn, ok) {
        setBusy(true);
        setMsg("");
        try {
            const d = await fn();
            if (d) apply(d);
            if (ok) notify(ok, "success");
            onChanged();
            return true;
        } catch (e) {
            const cur = api.staleCurrent(e);
            if (cur) { apply(cur); setMsg(STALE_MSG); onChanged(); }
            else setMsg(e.userMessage || api.errorText(e));
            return false;
        } finally {
            setBusy(false);
        }
    }

    const changeStatus = (code) => {
        if (busy || code === detail.status) return;
        if (code === "completed" && !form.process.trim()) { setProcErr(true); return; }
        if (needsReason(code)) { setPending(code); return; } // 사유를 받은 뒤 confirmReason 에서 보낸다
        setPending(null);
        run(async () => { const v = await flush(); return api.setStatus(id, v, code, undefined, undefined); });
    };
    // 사유 칸: 목표 상태(pending)로 바꾸기, 또는 이미 보류·취소인 문의의 사유만 저장
    const reasonTarget = pending || (needsReason(detail.status) ? detail.status : null);
    const confirmReason = () => {
        const target = reasonTarget;
        run(async () => { const v = await flush(); return api.setStatus(id, v, target, undefined, reason); }).then((ok) => { if (ok) setPending(null); });
    };
    const cancelReason = () => { setPending(null); setReason(detail.status_reason || ""); };
    const save = () => {
        const body = patchOf(detail, form);
        if (!Object.keys(body).length) { notify("바뀐 내용이 없습니다.", "info"); return; }
        run(async () => { await flush(); return null; }, "저장했습니다.");
    };
    const withFlush = (call, ok) => run(async () => { await flush(); return call(); }, ok);
    const toggleChat = async () => {
        if (chat.open) { setChat((c) => ({ ...c, open: false })); return; }
        setChat((c) => ({ ...c, open: true }));
        if (chat.messages === undefined) {
            try { const r = await api.getConversation(id); setChat({ open: true, messages: r ? r.messages : null }); }
            catch (e) { setChat({ open: true, messages: null }); setMsg(api.errorText(e)); }
        }
    };
    const uploadPhotos = (files, caps) =>
        withFlush(() => api.addAttachments(id, files, caps), `사진 ${files.length}장을 올렸습니다.`).then((ok) => { if (ok) setPicked(null); });

    return (
        <>
            <div className="dim on" {...dismiss}></div>
            <aside className="drawer on" role="dialog" aria-label={`문의 ${detail.id} 상세`}>
                <div className="dr-h">
                    <div className="dr-h-top">No. {detail.id} <ChannelBadge channel={detail.channel} />
                        {detail.reopened_from_id && <span className="tag tag-gen">재문의</span>}
                        <span>등록 {kstFull(detail.created_at)} · {registrarLabel(detail.registrar)}</span>
                        <button className="btn btn-ghost btn-sm x" onClick={onClose} aria-label="닫기"><i className="fa-solid fa-xmark"></i></button></div>
                    <h3>{detail.store?.name}</h3>
                    <div className="dr-meta">
                        <span><i className="fa-regular fa-id-card"></i>{bizFmt(detail.store?.business_number)}</span>
                        <span><i className="fa-solid fa-phone"></i>{phoneFmt(detail.store?.phone)}</span>
                        {isBot && detail.quick_category?.name && (
                            <span style={{ color: "var(--bot)" }}><i className="fa-regular fa-comment-dots"></i>챗봇 카테고리 「{detail.quick_category.name}」 → {detail.category?.label} · {detail.subcategory?.label} 자동 매핑</span>
                        )}
                    </div>
                    {detail.reopened_from && (
                        <div className="note" style={{ marginTop: 8 }}>
                            <i className="fa-solid fa-rotate-left"></i> 같은 문제가 다시 생겼다고 접수된 문의입니다 ·{" "}
                            <button className="fold" type="button" onClick={() => onOpen(detail.reopened_from.id)}>이전 문의 No.{detail.reopened_from.id}</button>
                            {detail.reopened_from.process_content && <> · 그때 처리: {detail.reopened_from.process_content}</>}
                        </div>
                    )}
                </div>
                <div className="dr-b">
                    {msg && <div className="err on" role="alert">{msg}</div>}
                    {fresh && (
                        <div className="err on" role="status">{FRESH_MSG}{" "}
                            <button className="btn btn-sm" onClick={() => apply(fresh)}>새로 불러오기</button></div>
                    )}
                    <div className="sec"><div className="sec-t">진행상태</div>
                        <div className="stbar">
                            {(meta?.statuses || []).map((s) => (
                                <button key={s.code} className={`s-${{ new: "new", processing: "ing", on_hold: "hold", completed: "done", canceled: "cancel" }[s.code]} ${(pending || detail.status) === s.code ? "on" : ""}`}
                                    disabled={busy} onClick={() => changeStatus(s.code)}>{s.label}</button>
                            ))}
                        </div>
                        <div className={`err ${procErr ? "on" : ""}`}>{PROC_MSG}</div>
                        {reasonTarget && (
                            <div className="fld" style={{ marginTop: 10 }}>
                                <label htmlFor="inq-d-reason">{statusLabel(meta, reasonTarget)} 사유 <span className="hint">선택 · {REASON_MAX}자</span></label>
                                <textarea id="inq-d-reason" className="ta" style={{ minHeight: 60 }} maxLength={REASON_MAX} aria-label={`${statusLabel(meta, reasonTarget)} 사유`} value={reason} onChange={(e) => setReason(e.target.value)}
                                    placeholder={reasonTarget === "canceled" ? "예: 중복 접수 · 가맹점주 요청으로 취소" : "예: 메뉴 사진 회신 대기"} />
                                <div className="note" style={{ margin: "6px 0 8px" }}><i className="fa-solid fa-mobile-screen"></i> 이 사유는 <b>가맹점주 앱에 그대로 보입니다</b>.</div>
                                <div className="row">
                                    {pending ? (
                                        <>
                                            <button className="btn btn-pri btn-sm" disabled={busy} onClick={confirmReason}>{statusLabel(meta, pending)}로 바꾸기</button>
                                            <button className="btn btn-sm" disabled={busy} onClick={cancelReason}>돌아가기</button>
                                        </>
                                    ) : (
                                        <button className="btn btn-sm" disabled={busy || reason === (detail.status_reason || "")} onClick={confirmReason}>사유 저장</button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="sec"><div className="sec-t">담당자 <span className="hint">한 명만 지정</span></div>
                        <div className="chips">
                            {(detail.assignees || []).length ? detail.assignees.map((a) => (
                                <span key={a.id} className="chip"><span className="ava">{a.name[0]}</span>{a.name} <small style={{ color: "var(--tx3)" }}>{a.team}</small>
                                    <button onClick={() => withFlush(() => api.removeAssignee(id, a.id))} aria-label={`${a.name} 빼기`} title="빼기" disabled={busy}><i className="fa-solid fa-xmark"></i></button></span>
                            )) : <span className="chip-none">미지정</span>}
                            {assignedIds.length <= 1 && addable.length > 0 && (
                                <select className="sel" style={{ height: 30 }} value="" aria-label={current == null ? "담당자 지정" : "담당자 변경"} disabled={busy}
                                    onChange={(e) => { if (e.target.value !== "") assignTo(Number(e.target.value)); }}>
                                    <option value="">{current == null ? "+ 담당자 지정" : "담당자 변경"}</option>
                                    {addable.map((g) => (
                                        <optgroup key={g.team} label={g.team}>{g.members.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</optgroup>
                                    ))}
                                </select>
                            )}
                            {assignedIds.length <= 1 && Number.isInteger(adminId) && current !== adminId && (
                                <button className="btn btn-sm" disabled={busy} onClick={() => (current == null
                                    ? withFlush(() => api.assignMe(id), "내가 맡은 문의로 표시했습니다.")
                                    : assignTo(adminId))}>내가 맡기</button>
                            )}
                        </div>
                        {assignedIds.length > 1 && <div className="note" style={{ margin: "6px 0 0" }}>담당자는 한 명만 둘 수 있습니다. 한 명만 남기고 빼 주세요.</div>}
                    </div>
                    <div className="sec grid2">
                        <div className="fld"><label htmlFor="inq-d-cat">구분</label>
                            <select id="inq-d-cat" className="sel" value={form.category}
                                onChange={(e) => { const c = (meta?.categories || []).find((x) => x.code === e.target.value); set({ category: e.target.value, sub: c?.subcategories?.[0]?.code || "" }); }}>
                                {(meta?.categories || []).map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                            </select></div>
                        <div className="fld"><label htmlFor="inq-d-sub">세부 항목</label>
                            <select id="inq-d-sub" className="sel" value={form.sub} onChange={(e) => set({ sub: e.target.value })}>
                                {(cat?.subcategories || []).map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
                            </select></div>
                        <div className="fld" style={{ gridColumn: "1/-1" }}><label>방문 일정</label>
                            <div className="row">
                                <input type="date" className="inp" aria-label="방문 날짜" value={form.date} onChange={(e) => set({ date: e.target.value })} style={{ maxWidth: 170 }} />
                                <input type="time" className="inp" aria-label="방문 시간" value={form.time} onChange={(e) => set({ time: e.target.value })} style={{ maxWidth: 120 }} />
                                <span className="note" style={{ margin: 0, alignSelf: "center" }}>정하면 가맹점주 앱에 "방문 예정"으로 보입니다</span>
                            </div></div>
                    </div>
                    {isBot && detail.ai_summary?.state === "ready" && (
                        <div className="sec"><div className="ai"><div className="ai-h">대화 요약 <span style={{ fontWeight: 400, color: "var(--tx3)", fontSize: 12 }}>챗봇 대화에서 자동 정리</span></div>
                            <ul>{(detail.ai_summary.lines || []).map((l, i) => <li key={i}>{l}</li>)}</ul></div></div>
                    )}
                    {isBot && detail.ai_summary?.state === "pending" && <div className="sec"><div className="note">대화 요약을 만드는 중입니다…</div></div>}
                    <div className="sec"><div className="sec-t">요청 내용 {isBot && <span className="hint">챗봇이 접수한 내용</span>}</div>
                        <textarea className="ta" aria-label="요청 내용" value={form.request} onChange={(e) => set({ request: e.target.value })} /></div>
                    {detail.chat_session_id && (
                        <div className="sec"><div className="sec-t">대화 원문 <button className="fold" onClick={toggleChat}>{chat.open ? "접기" : "펼치기"}</button></div>
                            {chat.open && (
                                <div className="chat">
                                    {chat.messages === undefined ? <div className="note">불러오는 중…</div>
                                        : !chat.messages || !chat.messages.length ? <div className="note">대화가 없습니다</div>
                                            : chat.messages.map((m, i) => <div key={i} className={`bub ${m.role === "user" ? "u" : "b"}`}>{m.content}</div>)}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="sec"><div className="sec-t">사진 첨부 <span className="hint">올린 사진·설명은 가맹점주에게도 보입니다</span></div>
                        <div className="imgs">
                            {images.map((im) => (
                                <a key={im.id} className="img" href={api.fileUrl(im.url)} target="_blank" rel="noreferrer" title={im.caption || ""}>
                                    <img src={api.fileUrl(im.url)} alt={im.caption || ""} /><span className="by">{im.caption ? `${uploaderLabel(im)} · ${im.caption}` : uploaderLabel(im)}</span></a>
                            ))}
                            {images.length < maxFiles && (
                                <button className="img-add" type="button" disabled={busy} onClick={() => fileRef.current?.click()}>
                                    <span><i className="fa-solid fa-plus" style={{ fontSize: 18 }}></i><br />사진 추가</span></button>
                            )}
                        </div>
                        <input ref={fileRef} type="file" accept="image/*" multiple hidden
                            onChange={(e) => { const files = Array.from(e.target.files); e.target.value = ""; if (files.length) setPicked(files.slice(0, Math.max(0, maxFiles - images.length))); }} />
                    </div>
                    <div className="sec"><div className="sec-t">처리 내용 <span className="hint">적으면 가맹점주 앱에 바로 보입니다</span></div>
                        <textarea className="ta" placeholder="무엇을 어떻게 처리했는지 적어주세요" value={form.process}
                            onChange={(e) => { set({ process: e.target.value }); if (procErr) setProcErr(false); }} /></div>
                    <div className="sec"><div className="sec-t">변경 기록 <span className="hint">자동으로 남습니다</span></div>
                        <ul className="tl">{(detail.logs || []).slice().reverse().map((l) => <li key={l.id} className={l.actor_type === "customer" ? "cust" : ""}>{l.text}<div className="w">{kstFull(l.at)} · {l.by}</div></li>)}</ul></div>
                    <div className="sec hist"><div className="sec-t">이 매장의 다른 문의 <span className="hint">{(detail.related || []).length}건</span></div>
                        {(detail.related || []).length ? detail.related.map((x) => (
                            <a href={`#inquiry-${x.id}`} key={x.id} onClick={(e) => { e.preventDefault(); onOpen(x.id); }}>
                                <StatusBadge status={x.status} meta={meta} /><span>{x.category?.label} · {x.subcategory?.label}</span>
                                <span style={{ color: "var(--tx2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 300 }}>{x.request_content}</span>
                                <span className="d">{kstShort(x.created_at)}</span></a>
                        )) : <div className="note">이전 문의가 없습니다</div>}
                    </div>
                </div>
                <div className="dr-f">
                    {owner && isBot && detail.chat_session_id && onGoChat && (
                        <button className="btn" onClick={() => onGoChat(detail.chat_session_id)}><i className="fa-solid fa-comment-dots"></i>대화 기록에서 보기</button>
                    )}
                    <span className="sp"></span>
                    <button className="btn" onClick={onClose}>닫기</button>
                    <button className="btn btn-pri" disabled={busy} onClick={save}><i className="fa-solid fa-floppy-disk"></i>저장</button>
                </div>
            </aside>
            {picked && <PhotoAddModal files={picked} maxFiles={Math.max(1, maxFiles - images.length)} busy={busy} onSubmit={uploadPhotos} onClose={() => setPicked(null)} />}
        </>
    );
}
